import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { FiPackage, FiX, FiSearch, FiLoader } from 'react-icons/fi';

const API_URL = process.env.REACT_APP_API_URL || 'https://green-admin.merket.io/api';

// Remove Vietnamese diacritics for search
const removeVietnameseDiacritics = (str) => {
  if (!str) return '';
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toLowerCase();
};

/**
 * ProductSearchSelect - Component tìm kiếm và chọn sản phẩm dùng chung
 * 
 * Hỗ trợ 2 chế độ:
 * 1. Static mode: Truyền products qua props
 * 2. API mode: Set useApi=true để fetch từ /api/menu/search
 * 
 * Hỗ trợ tìm kiếm không dấu tiếng Việt
 */
const ProductSearchSelect = ({
  // Static mode props
  products = [],
  
  // API mode props
  useApi = false,
  apiEndpoint = '/menu/search',
  apiParams = {}, // Additional params to pass to API
  
  // Common props
  value = '',
  initialProduct = null, // Pre-populated product for edit mode
  onChange,
  placeholder = 'Gõ để tìm sản phẩm...',
  label,
  showImage = true,
  showPrice = true,
  showCategory = true,
  showSelectedBadge = false,
  clearable = true,
  disabled = false,
  className = '',
  inputClassName = '',
  maxResults = 20,
  emptyText = 'Không có sản phẩm',
  notFoundText = 'Không tìm thấy sản phẩm',
  loadingText = 'Đang tải...',
  formatPrice,
  filterFn
}) => {
  const [searchText, setSearchText] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [apiProducts, setApiProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);
  const inputRef = useRef(null);
  const isTypingRef = useRef(false); // Track if user is typing
  const lastValueRef = useRef(value); // Track last external value
  const initializedRef = useRef(false); // Track if initial product was set

  // Set initial product on mount or when initialProduct changes (for edit mode)
  useEffect(() => {
    if (initialProduct && initialProduct.name) {
      setSelectedProduct(initialProduct);
      setSearchText(initialProduct.name || '');
      lastValueRef.current = initialProduct.id || initialProduct._id;
      initializedRef.current = true;
    } else if (!initialProduct && initializedRef.current) {
      // initialProduct was cleared (e.g., switching from edit to add mode)
      // Only reset if we were previously initialized with an initialProduct
    }
  }, [initialProduct?.id, initialProduct?._id, initialProduct?.name]);

  // Default price formatter
  const priceFormatter = formatPrice || ((price) => {
    return new Intl.NumberFormat('vi-VN').format(Math.round(price || 0));
  });

  // Safe get product ID
  const getProductId = (product) => {
    if (!product) return null;
    return product.id || product._id || null;
  };

  // Default filter function with Vietnamese diacritics support
  const filterFunction = filterFn || ((product, text) => {
    if (!product) return false;
    if (!text) return true;
    
    const searchNormalized = removeVietnameseDiacritics(text);
    const nameNormalized = removeVietnameseDiacritics(product.name || '');
    const categoryNormalized = removeVietnameseDiacritics(product.category || '');
    
    // Also check original text for exact match
    const searchLower = text.toLowerCase();
    const nameLower = (product.name || '').toLowerCase();
    const categoryLower = (product.category || '').toLowerCase();
    
    return nameNormalized.includes(searchNormalized) || 
           categoryNormalized.includes(searchNormalized) ||
           nameLower.includes(searchLower) ||
           categoryLower.includes(searchLower);
  });

  // Get products based on mode
  const getProducts = () => {
    if (useApi) {
      return apiProducts;
    }
    return Array.isArray(products) ? products.filter(p => p && p.name) : [];
  };

  const safeProducts = getProducts();

  // Fetch products from API
  const fetchProducts = useCallback(async (query = '') => {
    if (!useApi) return;
    
    setLoading(true);
    try {
      const params = { limit: maxResults, ...apiParams };
      if (query) {
        params.q = query;
      }
      
      const response = await axios.get(`${API_URL}${apiEndpoint}`, { params });
      setApiProducts(response.data.data || []);
      setHasLoaded(true);
    } catch (error) {
      console.error('Error fetching products:', error);
      setApiProducts([]);
    } finally {
      setLoading(false);
    }
  }, [useApi, apiEndpoint, maxResults, apiParams]);

  // Debounced search for API mode
  const debouncedSearch = useCallback((query) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    debounceRef.current = setTimeout(() => {
      fetchProducts(query);
    }, 300);
  }, [fetchProducts]);

  // Update selected product ONLY when value prop changes from outside
  useEffect(() => {
    // Skip if user is typing or value hasn't changed
    if (isTypingRef.current || value === lastValueRef.current) {
      return;
    }
    
    lastValueRef.current = value;
    
    if (value && safeProducts.length > 0) {
      const found = safeProducts.find(p => {
        const pid = getProductId(p);
        return pid === value || String(pid) === String(value);
      });
      if (found) {
        setSelectedProduct(found);
        setSearchText(found.name || '');
      }
    } else if (!value) {
      setSelectedProduct(null);
      setSearchText('');
    }
  }, [value]);

  // Sync selected product when products load (for edit mode)
  useEffect(() => {
    if (value && safeProducts.length > 0 && !selectedProduct && !isTypingRef.current) {
      const found = safeProducts.find(p => {
        const pid = getProductId(p);
        return pid === value || String(pid) === String(value);
      });
      if (found) {
        setSelectedProduct(found);
        setSearchText(found.name || '');
      }
    }
  }, [safeProducts.length]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const handleFocus = () => {
    setShowSuggestions(true);
    
    // For API mode: Load initial products on first focus
    if (useApi && !hasLoaded && !loading) {
      fetchProducts('');
    }
  };

  const handleBlur = () => {
    // Reset typing flag after a delay to allow click on suggestions
    setTimeout(() => {
      isTypingRef.current = false;
    }, 200);
  };

  const handleSearch = (text) => {
    if (!text) {
      setSelectedProduct(null);
      if (onChange) onChange(null);
      
      // For API mode: Reset to initial products
      if (useApi) {
        fetchProducts('');
      }
    } else if (useApi) {
      // For API mode: Debounced search
      debouncedSearch(text);
    }
  };

  const handleInputChange = (e) => {
    const text = e.target.value;
    isTypingRef.current = true; // Mark that user is typing
    setSearchText(text);
    setShowSuggestions(true);
    handleSearch(text);
  };

  const handleSelectProduct = (product) => {
    isTypingRef.current = false; // User finished typing by selecting
    lastValueRef.current = getProductId(product); // Update last value
    initializedRef.current = true; // Mark as initialized
    setSelectedProduct(product);
    setSearchText(product?.name || '');
    setShowSuggestions(false);
    if (onChange) onChange(product);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    isTypingRef.current = false;
    lastValueRef.current = null;
    initializedRef.current = false; // Allow re-initialization
    setSelectedProduct(null);
    setSearchText('');
    setShowSuggestions(false);
    if (onChange) onChange(null);
  };

  // Filter products (for static mode only)
  const filteredProducts = useApi 
    ? safeProducts 
    : safeProducts.filter(p => filterFunction(p, searchText)).slice(0, maxResults);

  const selectedId = getProductId(selectedProduct);

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      {label && (
        <label className="block text-xs text-gray-500 mb-1">{label}</label>
      )}
      
      <div className="relative">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
          {loading ? (
            <FiLoader size={16} className="animate-spin" />
          ) : (
            <FiSearch size={16} />
          )}
        </div>
        
        <input
          ref={inputRef}
          type="text"
          value={searchText}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          className={`w-full pl-9 pr-8 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 ${
            disabled ? 'bg-gray-100 cursor-not-allowed' : ''
          } ${inputClassName}`}
        />
        
        {clearable && selectedProduct && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
          >
            <FiX size={16} />
          </button>
        )}
      </div>

      {/* Suggestions dropdown */}
      {showSuggestions && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg max-h-[280px] overflow-y-auto z-50">
          {loading ? (
            <div className="px-3 py-4 text-center text-gray-500 text-sm flex items-center justify-center gap-2">
              <FiLoader className="animate-spin" />
              {loadingText}
            </div>
          ) : safeProducts.length === 0 ? (
            <div className="px-3 py-4 text-center text-gray-500 text-sm">
              {hasLoaded || !useApi ? emptyText : 'Click để tải sản phẩm...'}
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="px-3 py-4 text-center text-gray-500 text-sm">
              {notFoundText}
            </div>
          ) : (
            filteredProducts.map((product, index) => {
              const productId = getProductId(product);
              const isSelected = selectedId && productId && String(selectedId) === String(productId);
              
              return (
                <div
                  key={productId || `product-${index}`}
                  onClick={() => handleSelectProduct(product)}
                  className={`px-3 py-2 hover:bg-green-50 cursor-pointer border-b last:border-b-0 flex items-center gap-2 ${
                    isSelected ? 'bg-green-50' : ''
                  }`}
                >
                  {showImage && (
                    product.image ? (
                      <img 
                        src={product.image} 
                        alt="" 
                        className="w-8 h-8 rounded object-cover flex-shrink-0" 
                      />
                    ) : (
                      <div className="w-8 h-8 rounded bg-gray-100 flex items-center justify-center flex-shrink-0">
                        <FiPackage size={14} className="text-gray-400" />
                      </div>
                    )
                  )}
                  
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{product.name}</div>
                    {(showCategory || showPrice) && (
                      <div className="text-xs text-gray-500">
                        {showCategory && product.category}
                        {showCategory && showPrice && product.category && ' • '}
                        {showPrice && priceFormatter(product.price)}
                      </div>
                    )}
                  </div>
                  
                  {!showCategory && !showImage && showPrice && (
                    <span className="text-sm text-gray-500 flex-shrink-0">
                      {priceFormatter(product.price)}
                    </span>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Selected badge (optional) */}
      {showSelectedBadge && selectedProduct && !showSuggestions && (
        <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg flex justify-between items-center">
          <span className="text-sm text-green-700 flex items-center gap-1">
            {showImage && selectedProduct.image && (
              <img src={selectedProduct.image} alt="" className="w-5 h-5 rounded object-cover" />
            )}
            {selectedProduct.name}
          </span>
          {showPrice && (
            <span className="text-sm font-medium text-green-700">
              {priceFormatter(selectedProduct.price)}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductSearchSelect;
